// University of Florida: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - UF's Common Data Set 2025-2026 (UF Main Campus), posted by
//    Institutional Planning and Research on its CDS Reports page (file
//    "CDS 2025-26_v8_08.14.2026.pdf" on data-apps.ir.aa.ufl.edu): C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 plans, G1/G5 2026-2027
//    costs. C21 gives no early decision counts for Fall 2025.
//  - UF Admissions: Freshman Dates & Deadlines ("Application Deadlines &
//    Options 2026-27", the fall 2027 cycle), Early Decision FAQ, How to Apply
//    and Requirements for Freshman Applicants. The CDS gives the Early
//    Action notification as 1/23; the admissions page says January 22, which
//    is used here. The Early Decision FAQ prints the confirmation deadline
//    as "January 8, 2026"; the deadlines table says January 8 for this
//    cycle, so the year is left off here.
//  - College Scorecard, school id 134130 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items are used. The CDS's own out-of-state tuition
// and fees figure ($34,616) is $1 below the sum of its tuition ($31,316)
// and fees ($3,301). The financial aid office's 2026-27 budget for
// incoming freshmen ($25,830 in-state, $54,010 out-of-state) rounds
// tuition and fees, uses higher books and lower personal estimates, and
// adds $56 in federal loan fees.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UF Common Data Set 2025-2026',
        url: 'https://ir.aa.ufl.edu/reports/cds-reports/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UF Admissions, Freshman Dates & Deadlines',
        url: 'https://admissions.ufl.edu/apply/freshman/deadlines',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'UF Admissions, Early Decision',
        url: 'https://admissions.ufl.edu/apply/freshman/early-decision',
        read: '2026-10-01',
    },
    howToApply: {
        label: 'UF Admissions, Freshman How to Apply',
        url: 'https://admissions.ufl.edu/apply/freshman/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'UF Admissions, Requirements for Freshman Applicants',
        url: 'https://admissions.ufl.edu/apply/freshman/requirements',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?134130-University-of-Florida',
        read: '2026-10-15',
    },
    coa: {
        label: 'UF Student Financial Affairs, 2026-27 Undergraduate Cost of Attendance',
        url: 'https://www.sfa.ufl.edu/cost/',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-florida',
    kbId: 'university_of_florida',
    name: 'University of Florida',
    shortName: 'UF',
    location: 'Gainesville, FL',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (UF Brand Center, Core Orange and Core Blue); decoration only.
    branding: {
        primary: '#FA4616',
        secondary: '#0021A5',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_florida_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 90523,
        admitted: 18403,
        enrolled: 7619,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 42172, admitted: 9387 },
            { group: 'Out-of-state', applied: 39420, admitted: 8086 },
            { group: 'International', applied: 8931, admitted: 930 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UF does not admit by major, and its Common Data Set reports one first-year acceptance rate for the whole university.',
        source: 'earlyDecision',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UF offers binding Early Decision and non-binding Early Action for fall 2027. The Common Data Set leaves the Fall 2025 early decision counts blank, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'UF does not admit by major. A few choices add a step to your Common App:',
        rows: [
            { school: 'Honors Program', requirement: 'Say you are interested on the Common App and answer the additional Honors essay question. Early Decision applicants may apply too.', source: 'howToApply' },
            { school: 'Innovation Academy', requirement: 'A degree pathway built around entrepreneurship, ethics and leadership that runs on a spring and summer schedule. To apply, indicate interest, write an additional essay and select a major within the academy, all in the Common App.', source: 'howToApply' },
            { school: 'College of the Arts', requirement: 'Some majors require an audition. Early Decision stays binding even if you are not admitted to that major, because UF does not admit by major.', source: 'earlyDecision' },
        ],
        source: 'howToApply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision', deadline: 'October 15', decision: 'December 11', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'January 22', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 19', binding: false },
        ],
        note: 'Materials (your STARS or transcript, and your test scores, entered by you or sent officially) are due one week after each deadline: October 22, November 8 and January 22. Early Decision admits confirm by January 8; Early Action and Regular Decision admits by May 1. Official test scores are due May 1 and official transcripts July 1. You apply only through the Common App, and may ask to start in Summer B or fall.',
        source: 'deadlines',
        binding: 'Early Decision is binding: the Common App asks the student, a parent and the high school counselor to sign an agreement. UF uses single-choice Early Decision, so you cannot apply Early Decision anywhere else. If admitted, you confirm by the deadline and withdraw your other applications. A deferred applicant is released from the agreement and reviewed in Regular Decision.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'All applicants must submit SAT, ACT or Classic Learning Test (CLT) scores, and UF has no test preference. It superscores within each test, combining your highest section scores across dates; the ACT superscore counts English, Reading and Math only, not Science. You may enter your own scores in the Gator Portal, with official scores due May 1.',
        source: 'requirements',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, and notes that UF also accepts the CLT.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [6440, 34620] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1220, 1220] },
            { item: 'Transportation', amounts: [1700, 1700] },
            { item: 'Living expenses', amounts: [14190, 14190] },
            { item: 'Personal expenses', amounts: [2224, 2224] },
            { item: 'Federal student loan fees', amounts: [56, 56] },
        ],
        totals: [25830, 54010],
        note: 'Projected for first-time freshmen taking a full fall and spring load (30 hours).',
        source: 'coa',
        netPrice: {
            average: 5969,
            byIncome: [
                { income: '$0 to $30,000', amount: 1968 },
                { income: '$30,001 to $48,000', amount: 1809 },
                { income: '$48,001 to $75,000', amount: 5221 },
                { income: '$75,001 to $110,000', amount: 10235 },
                { income: 'Over $110,000', amount: 16609 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UF\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
